// SPDX-License-Identifier: LicenseRef-Techsafe-Proprietary
// Copyright (c) 2026 Techsafe Teknoloji ve Muhendislik A.S.
// https://techsafe-solutions.com
// Economic rights: Techsafe A.S.
// Authors' moral rights reserved under Turkish Copyright Law (FSEK 5846).
// Mali haklar Techsafe A.S.'ye aittir; eser sahiplerinin manevi haklari saklidir (FSEK 5846).
// DRAFT AI-assistance notice (IP, License & Attribution Policy v1.3 §12d-1):
// This content was prepared with the assistance of AI tools and is, on this
// branch, a DRAFT pending human review. On the human-approved merge of its
// Merge Request this notice flips to the published variant (§12d-2).
// Bu icerik yapay zeka destekli araclarla hazirlanmis bir TASLAKTIR (§12d-1);
// insan onayiyla birlestirilmesinde yayimlanmis surumune (§12d-2) donusur.

/**
 * Gateway client factory for the widget runtime.
 *
 * The composition-root wiring of the concrete postMessage transport
 * (GatewayClient) for AU0009. The per-tier Gateway origin and the active tier
 * are read from the build/runtime environment (Vite `import.meta.env`) and
 * validated through `config.ts` — never hardcoded, never a silent "*" fallback
 * (ADR-002 P4/P6 origin discipline; ISO/IEC 5055 explicit configuration
 * handling). The injected `GatewayEnvironment` keeps the transport free of
 * hidden globals so it stays unit-testable.
 *
 * Environment contract (set per tier at build/deploy time):
 *   VITE_BRIDGE_TIER            — "dev" | "stage" | "prod"
 *   VITE_BRIDGE_GATEWAY_ORIGIN  — absolute https origin of the Widget API Gateway
 */

import {
  asBridgeTier,
  makeOriginConfig,
  type BridgeOriginConfig,
  type BridgeTier,
} from "@/bridge/config";
import { GatewayClient, type InboundMessageEvent } from "./gateway-client";

/** Per-request Gateway timeout, ms. */
const GATEWAY_TIMEOUT_MS = 5000;

/**
 * Resolve the origin configuration from the build/runtime environment.
 * When env vars are absent (no .env file), falls back to sensible dev defaults:
 * tier = "dev", gateway origin = the most known gateway url. This keeps the widget
 * startable without any env file for local development; production deployments
 * MUST set explicit values via Cloudflare Pages env vars or .env.
 */
function resolveOriginConfig(): BridgeOriginConfig {
  const env =
    (import.meta as unknown as { env?: Record<string, string | undefined> })
      .env ?? {};

  const rawTier = env["VITE_BRIDGE_TIER"];
  const tier: BridgeTier =
    rawTier !== undefined && rawTier.length > 0
      ? (asBridgeTier(rawTier.trim()) ?? "dev")
      : "dev";

  const rawOrigin = env["VITE_BRIDGE_GATEWAY_ORIGIN"];
  const gatewayOrigin =
    rawOrigin !== undefined && rawOrigin.length > 0
      ? rawOrigin.trim()
      : "https://zoho-widget-api-gateway.iot-ide.com";

  return makeOriginConfig(tier, gatewayOrigin);
}

export function createRealGatewayClient(): GatewayClient {
  const origin = resolveOriginConfig();

  return new GatewayClient({
    origin,
    timeoutMs: GATEWAY_TIMEOUT_MS,
    env: {
      discoverableTargets() {
        // Walk the parent frame tree upward. The widget may be nested inside
        // multiple iframes; the Gateway may reside at any ancestor level, not
        // just among direct siblings. Enumerate every frame at every ancestor
        // level, deduplicating by identity.
        const seen = new Set<Window>();
        seen.add(window as unknown as Window);
        const targets: Window[] = [];

        let current: Window | null = window as unknown as Window;
        let parent: Window | null = window.parent as unknown as Window;

        while (parent && parent !== current) {
          try {
            for (let i = 0; i < parent.frames.length; i += 1) {
              const frame = parent.frames[i] as unknown as Window;
              if (!seen.has(frame)) {
                seen.add(frame);
                targets.push(frame);
              }
            }
          } catch {
            // Cross-origin access to parent.frames may throw; skip this level.
          }
          seen.add(parent);
          current = parent;
          parent = (parent as Window).parent as unknown as Window | null;
        }

        return targets;
      },
      addMessageListener(handler) {
        const listener = (event: MessageEvent) => {
          handler({ origin: event.origin, data: event.data } satisfies InboundMessageEvent);
        };
        window.addEventListener("message", listener);
        return () => window.removeEventListener("message", listener);
      },
      setTimer(callback, ms) {
        return window.setTimeout(callback, ms) as unknown as number;
      },
      clearTimer(handle) {
        window.clearTimeout(handle as unknown as number);
      },
      newUUID() {
        return globalThis.crypto?.randomUUID?.() ?? `${Date.now()}-${Math.random()}`;
      },
    },
  });
}
